import React, { forwardRef, useCallback, type ComponentProps } from "react";

import { createUseStyles } from "react-jss";
import { QueryBuilderComponent as SyncfusionQueryBuilderComponent } from "@syncfusion/ej2-react-querybuilder";
// Every prop is forwarded verbatim to Syncfusion's QueryBuilder.
type QueryBuilderProps = ComponentProps<typeof SyncfusionQueryBuilderComponent>;

const QueryBuilderComponent = forwardRef((props: QueryBuilderProps, ref: React.Ref<unknown>) => {
  // The ref is deliberately `Ref<unknown>`: callers declare their own structural handle for the
  // Syncfusion instance (see AddRule.tsx). A ref object is invariant in its target, so the caller's
  // ref cannot be handed to Syncfusion's `Ref<QueryBuilderComponent>` prop as-is — attaching through
  // a callback stores the very same instance and needs no assertion, since writing a
  // `QueryBuilderComponent` into a `Ref<unknown>` is sound.
  const attachRef = useCallback(
    (instance: SyncfusionQueryBuilderComponent | null) => {
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    },
    [ref],
  );

  const useStyles = createUseStyles({
    QueryBuilderComponent: {
      "& .e-rule-filter, & .e-rule-operator.e-operator, & .e-rule-value.e-value": {
        width: "26% !important",
        minWidth: "9.375rem",
      },
      "& .e-query-builder": {
        border: "none",
      },
      "& .e-query-builder .e-rule-container": {
        backgroundColor: "#fff",
      },
    },
  });

  const classes = useStyles() as Record<"QueryBuilderComponent", string>;

  return (
    <div className={classes.QueryBuilderComponent} id="QueryBuilderComponentWrapper">
      <SyncfusionQueryBuilderComponent {...props} className={classes.QueryBuilderComponent} ref={attachRef}>
        {props.children}
      </SyncfusionQueryBuilderComponent>
    </div>
  );
});
QueryBuilderComponent.displayName = "QueryBuilderComponent";
export default QueryBuilderComponent;
